import React, { createContext, useContext, useState, useReducer, useEffect } from 'react';
import { useAuth } from './AuthContext';

// Création du contexte d'application
const AppContext = createContext();

// Actions
const actions = {
  SET_MEETINGS: 'SET_MEETINGS',
  ADD_MEETING: 'ADD_MEETING',
  UPDATE_MEETING: 'UPDATE_MEETING',
  DELETE_MEETING: 'DELETE_MEETING',
  SET_TRANSCRIPTIONS: 'SET_TRANSCRIPTIONS',
  ADD_TRANSCRIPTION: 'ADD_TRANSCRIPTION',
  UPDATE_TRANSCRIPTION: 'UPDATE_TRANSCRIPTION',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_THEME: 'SET_THEME',
};

// État initial
const initialState = {
  meetings: [],
  transcriptions: [],
  loading: false,
  error: null,
  theme: 'light',
};

// Réducteur pour gérer les actions
const reducer = (state, action) => {
  switch (action.type) {
    case actions.SET_MEETINGS:
      return { ...state, meetings: action.payload };
    case actions.ADD_MEETING:
      return { ...state, meetings: [...state.meetings, action.payload] };
    case actions.UPDATE_MEETING:
      return {
        ...state,
        meetings: state.meetings.map((meeting) =>
          meeting.id === action.payload.id ? action.payload : meeting
        ),
      };
    case actions.DELETE_MEETING:
      return {
        ...state,
        meetings: state.meetings.filter((meeting) => meeting.id !== action.payload),
      };
    case actions.SET_TRANSCRIPTIONS:
      return { ...state, transcriptions: action.payload };
    case actions.ADD_TRANSCRIPTION:
      return { ...state, transcriptions: [...state.transcriptions, action.payload] };
    case actions.UPDATE_TRANSCRIPTION:
      return {
        ...state,
        transcriptions: state.transcriptions.map((transcription) =>
          transcription.id === action.payload.id ? action.payload : transcription
        ),
      };
    case actions.SET_LOADING:
      return { ...state, loading: action.payload };
    case actions.SET_ERROR:
      return { ...state, error: action.payload };
    case actions.CLEAR_ERROR:
      return { ...state, error: null };
    case actions.SET_THEME:
      return { ...state, theme: action.payload };
    default:
      return state;
  }
};

// Hook personnalisé pour utiliser le contexte d'application
export const useApp = () => {
  return useContext(AppContext);
};

// Fournisseur du contexte d'application
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { currentUser } = useAuth();

  // Effet pour récupérer le thème des préférences utilisateur
  useEffect(() => {
    if (currentUser && currentUser.profile && currentUser.profile.settings) {
      const userTheme = currentUser.profile.settings.theme;
      if (userTheme) {
        setTheme(userTheme);
      }
    }
  }, [currentUser]);

  // Actions utilisables dans l'application
  const setMeetings = (meetings) => {
    dispatch({ type: actions.SET_MEETINGS, payload: meetings });
  };

  const addMeeting = (meeting) => {
    dispatch({ type: actions.ADD_MEETING, payload: meeting });
  };

  const updateMeeting = (meeting) => {
    dispatch({ type: actions.UPDATE_MEETING, payload: meeting });
  };

  const deleteMeeting = (meetingId) => {
    dispatch({ type: actions.DELETE_MEETING, payload: meetingId });
  };

  const setTranscriptions = (transcriptions) => {
    dispatch({ type: actions.SET_TRANSCRIPTIONS, payload: transcriptions });
  };

  const addTranscription = (transcription) => {
    dispatch({ type: actions.ADD_TRANSCRIPTION, payload: transcription });
  };

  const updateTranscription = (transcription) => {
    dispatch({ type: actions.UPDATE_TRANSCRIPTION, payload: transcription });
  };

  const setLoading = (isLoading) => {
    dispatch({ type: actions.SET_LOADING, payload: isLoading });
  };

  const setError = (error) => {
    dispatch({ type: actions.SET_ERROR, payload: error });
  };

  const clearError = () => {
    dispatch({ type: actions.CLEAR_ERROR });
  };

  const setTheme = (theme) => {
    dispatch({ type: actions.SET_THEME, payload: theme });
    // Stocker également le thème dans localStorage pour persistance
    localStorage.setItem('app_theme', theme);
  };

  const value = {
    ...state,
    setMeetings,
    addMeeting,
    updateMeeting,
    deleteMeeting,
    setTranscriptions,
    addTranscription,
    updateTranscription,
    setLoading,
    setError,
    clearError,
    setTheme,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}; 